import { format, isAfter, subHours } from "date-fns";
import { sv } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { useGlobalContext } from "app/store/GlobalContext";
import { splitName } from "common/splitName/splitName";
import { Icon } from "components/icon/Icon";
import Persona from "components/persona/Persona";
import { filterAndSortMessages } from "helpers/inbox/messageAndThreadHelpers";
import { usePermission } from "hooks/use-permissions";
import { extractThreadUUID } from "helpers/inbox/messageQueryParam";
import { useCleanBody } from "hooks/use-clean-body";
import {
  MessageItem,
  THREAD_CATEGORY_LABELS,
  ThreadCategory,
  Thread as ThreadModel
} from "models/inbox/inbox";
import { useRootStore } from "store";

import { Attach16Regular } from "@fluentui/react-icons";
import ThreadSidebarMessageItem from "../threadSiedebarMessageItem";
import "./ThreadSidebarItem.scss";

type Props = {
  thread: ThreadModel;
  threadLink: string;
  isSubscribing: boolean;
};

export default function ThreadSidebarItem({ thread, threadLink }: Props) {
  const { handleNavigation } = useGlobalContext();
  const [toggleMessages, setToggleMessages] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const {
    threadId,
    unread,
    title,
    lastMessage,
    category = ThreadCategory.None
  } = thread;

  const { threadId: currentThreadParam } = useParams();
  const isActive =
    currentThreadParam && extractThreadUUID(threadId) === currentThreadParam;

  const { fetchThread } = useRootStore((state) => state.inbox);

  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );

  const { customerNumber: customerId } = customer;

  const isInternal = usePermission("isInternal");

  const { messages: visibleMessages } = useMemo(() => {
    return filterAndSortMessages(thread?.messages, isInternal);
  }, [thread.messages, isInternal]);

  const hasAttachments = useMemo(() => {
    return visibleMessages.some(
      (message) => message.attachments && message.attachments.length > 0
    );
  }, [visibleMessages]);

  const { formatedFrom, createdAt, body } = lastMessage;

  const sender = splitName(formatedFrom);

  const yesterday = subHours(new Date(), 24);
  const messageDate = new Date(createdAt);
  const isRecent = isAfter(messageDate, yesterday);

  const displayDate = isRecent
    ? format(messageDate, "HH:mm")
    : format(messageDate, "yyyy/MM/dd", { locale: sv });

  const cleanedBody = useCleanBody(body) || "...";

  const handleSelectThread = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const target = e.target as HTMLElement;
    if (target.closest(".thread-sidebar-message-wrapper")) {
      return;
    }

    handleNavigation({
      url: threadLink,
      eventTitle: "Thread",
      e: e,
      clickEvent() {
        if (window.location.pathname === threadLink) {
          fetchThread(customerId, threadId);
        }
        // Clear selectedMessageId when clicking thread (not a specific message)
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("selectedMessageId");
          return newParams;
        });
      }
    });
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setToggleMessages((prev) => !prev);
  };

  const ThreadSidebarMessageList = () => (
    <>
      {visibleMessages.map((message: MessageItem) => {
        return (
          <ThreadSidebarMessageItem
            message={message}
            key={message.messageId}
            threadId={threadId}
            threadLink={threadLink}
          />
        );
      })}
    </>
  );

  return (
    <div
      className={`d-flex flex-direction-column thread-sidebar-wrapper ${isActive ? "sidebar-item-active" : ""}`}
    >
      <div
        className={`sidebar-item d-flex ${unread ? "sidebar-item-unread" : ""}`}
        onClick={(e) => handleSelectThread(e)}
      >
        <div className="thread-meta mt-3 mr-2 ml-2">
          <span className="thread-meta-arrow">
            {visibleMessages.length > 1 ? (
              <Icon
                name={`fas ${toggleMessages ? "fa-chevron-down" : "fa-chevron-right"} cursor-pointer`}
                onClick={handleArrowClick}
              />
            ) : null}
          </span>
        </div>

        <div className="d-flex align-items-start w-100">
          <Persona
            firstName={sender.firstName}
            lastName={sender.lastName}
            className="mr-3 mt-2 message-person"
          />

          <div className="d-flex flex-direction-column w-100">
            <div className="d-flex justify-content-between mt-1">
              <div className="d-flex align-items-center gap-2">
                <span className="mr-3 message-sender">{formatedFrom}</span>
                {category !== ThreadCategory.None && isInternal && (
                  <span
                    className={`category-badge category-badge-${ThreadCategory[category].toLowerCase()}`}
                  >
                    {THREAD_CATEGORY_LABELS[category]}
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                {hasAttachments && <Attach16Regular />}
                <div className="message-time fw-normal">{displayDate}</div>
              </div>
            </div>

            <div className="d-flex justify-content-between my-1 html-editor-truncate-text">
              <span className="sidebar-item-title">{title}</span>
            </div>

            <div className="sidebar-item-message html-editor html-editor-truncate-text">
              {cleanedBody}
            </div>

            {toggleMessages && ThreadSidebarMessageList()}
          </div>
        </div>
      </div>
    </div>
  );
}
